/**
 * TextField - Base text input field
 *
 * Foundation for all text-based inputs (email, phone, url, etc.)
 */

import { useState, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import { FormField } from '../FormField';
import { formStyles, mergeStyles } from '../formStyles';

export interface TextFieldProps {
  /** Field name attribute */
  name: string;
  /** Field label */
  label: string;
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Mark as required */
  required?: boolean;
  /** Error message */
  error?: string;
  /** Icon to display inside input */
  icon?: LucideIcon;
  /** Disable the field */
  disabled?: boolean;
  /** HTML autocomplete attribute */
  autoComplete?: string;
  /** Input type (text, email, tel, url) */
  type?: 'text' | 'email' | 'tel' | 'url' | 'password';
  /** Auto focus on mount */
  autoFocus?: boolean;
  /** Help text */
  hint?: string;
  /** Max length */
  maxLength?: number;
  /** Custom className */
  className?: string;
}

export function TextField({
  name,
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  icon: Icon,
  disabled,
  autoComplete,
  type = 'text',
  autoFocus,
  hint,
  maxLength,
  className,
}: TextFieldProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const inputStyle = mergeStyles(
    formStyles.input,
    Icon && formStyles.inputWithIcon,
    isFocused && formStyles.inputFocus,
    error && formStyles.inputError,
    disabled && formStyles.inputDisabled
  );

  return (
    <FormField label={label} required={required} error={error} hint={hint} className={className}>
      <div style={formStyles.inputWrapper}>
        {Icon && (
          <span style={formStyles.inputIcon}>
            <Icon size={16} />
          </span>
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          maxLength={maxLength}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={inputStyle}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
        />
      </div>
    </FormField>
  );
}
